import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Alert, Box, Typography, Unstable_Grid2 as Grid} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointLoyaltyDistributionDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import StatCard from '../../components/stat-card';
import StatusPill from '../../components/status-pill';
import {paginateMockItems} from '../../mock-search';
import {mockBusiness, mockDistributionActivity} from '../../mock-data';

const dataGridSaveConfig = {name: 'BizPointLoyaltyDistributionDataGrid', version: 2};
const col = BizPointLoyaltyDistributionDataGridColumnNames;

const searchOptions = {
    keywordFields: ['customer', 'rule', 'status', 'reviewReason'],
    sortFieldMap: {
        [col.date]: 'date',
        [col.customer]: 'customer',
        [col.rule]: 'rule',
        [col.biz]: 'biz',
    },
};

const formatStatusLabel = (row, t) => {
    if (row.status === 'Frozen' && row.freezeUntil) {
        return t('frozenUntil', {date: row.freezeUntil});
    }
    if (row.status === 'In review' && row.reviewReason) {
        return t('inReviewReason', {reason: row.reviewReason});
    }
    return row.status;
};

const LoyaltyDashboardView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.dashboard'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportLoyaltyDashboard = operatorAccessLevels.packages.bizPoint.exportLoyaltyDashboard;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-loyalty-dashboard');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [data, setData] = useState([]);

    /**
     * Configures notebook tags and behavioral analytics for the loyalty dashboard.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointLoyaltyDashboard);
        configureBehavioralTool((tags) => tags.bizPointLoyaltyDashboard);
    }, [configureBehavioralTool, configureNotebookTags]);

    const burnWeeks = useMemo(
        () => Math.round(mockBusiness.balance / mockBusiness.rateBizPer100 * 100 / 10) * 10,
        [],
    );

    const lowBalanceWarning = useMemo(
        () => mockBusiness.balance < 5000,
        [],
    );

    const columns = useMemo(() => [
        {title: t('date'), name: col.date, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('customer'), name: col.customer, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('rule'), name: col.rule, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('biz'), name: col.biz, type: DataGridColumnTypes.element, alignment: DataGridColumnAlignments.right, sortable: true},
        {title: t('status'), name: col.status, type: DataGridColumnTypes.element, alignment: DataGridColumnAlignments.left, sortable: false, width: 200},
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads distribution activity from mock data.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(mockDistributionActivity, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(mockDistributionActivity, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {
            },
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {
            [col.biz]: (item) => String(item.biz),
            [col.status]: (item) => {
                const parts = [formatStatusLabel(item, t)];
                if (item.unclaimed) {
                    parts.push(t('unclaimed'));
                }
                return parts.join(' / ');
            },
        };
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, t]);

    const rows = useMemo(() => data.map((row) => ({
        key: `${row.date}-${row.customer}`,
        data: row,
        cells: {
            [col.date]: row.date,
            [col.customer]: row.customer,
            [col.rule]: row.rule,
            [col.biz]:
                <Typography
                    variant="body2"
                    fontWeight={600}
                    color={row.biz < 0 ? 'error.main' : 'success.main'}
                >
                    {row.biz > 0 ? `+${row.biz}` : row.biz}
                </Typography>,
            [col.status]:
                <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                    <StatusPill status={row.status} label={formatStatusLabel(row, t)}/>
                    {row.unclaimed && <StatusPill flag status="unclaimed" label={t('unclaimed')}/>}
                </Box>,
        },
    })), [data, t]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            {lowBalanceWarning && (
                <Alert severity="warning" sx={{mb: 2}}>
                    {t('lowBalanceWarning')}
                </Alert>
            )}

            <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1.5}}>
                {t('summary')}
            </Typography>
            <Grid container spacing={1} sx={{justifyContent: 'space-evenly', mb: 3}}>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('undistributedBiz')}
                        value={mockBusiness.balance.toLocaleString()}
                        valueAdornment={mockBusiness.currency}
                        note={t('burnWeeksNote', {weeks: burnWeeks})}
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('purchaseRate')}
                        value={String(mockBusiness.rateBizPer100)}
                        valueAdornment={t('rateUnit')}
                        note={t('tierSpreadNote', {tier: mockBusiness.tier, spread: mockBusiness.spread})}
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('freezeDays')}
                        value={t('freezeDaysValue', {days: mockBusiness.freezeDays})}
                        note={t('freezeAppliesNote')}
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('distributedLast30d')}
                        value={mockBusiness.distributedLast30d.toLocaleString()}
                        note={t('distributedCustomersNote', {count: mockBusiness.distributedCustomers})}
                    />
                </Grid>
            </Grid>

            <Box sx={{mb: 3}}>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={tSearch('keyword')}
                />
            </Box>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                getExportInfo={canExportLoyaltyDashboard ? getExportInfo : undefined}
            />
        </Box>
    );
};

export default LoyaltyDashboardView;
